'use client';

import {Scroller} from '@shared/Scroller/Scroller';

import AllCategoryIcon from '@web-catalog/assets/icons/all_categories_outline_24.svg';
import SparkleIcon from '@web-catalog/assets/icons/sparkle_24.svg';

import {
  ALL_APPS_TITLE,
  ALL_GAMES_TITLE,
  INTERESTING_TITLE,
} from '@web-catalog/const';

import {AppCategory, AppType, NO_CATEGORY_CATEGORY} from '@web-catalog/types';

import {useFilters} from '@web-catalog/hooks/useFilters';

import {useCategoriesAnalytics} from '@web-catalog/utils/analytics/useCategoriesAnalytics';

import styles from './Categories.module.scss';
import {Chip} from './Chip';
import {useSplitCategoriesToChunks} from './useSplitCategoriesToChunks';

type Props = {
  appType: AppType;
  activeCategory: string | null;
  categories: AppCategory[];
};

const getLink = (category: string, appType: AppType) =>
  `${appType === AppType.GAMES ? '/games' : ''}/${category}`;

export const Categories = ({categories, appType, activeCategory}: Props) => {
  const logger = useCategoriesAnalytics('bubble');
  const {getFullValue} = useFilters();

  const categoryChunks = useSplitCategoriesToChunks(categories, appType);

  const renderCategory = (category: AppCategory) =>
    category.appCount === 0 ? null : (
      <Chip
        key={category.category}
        linkTo={
          getLink(category.category.toLowerCase(), appType) + getFullValue()
        }
        label={category.categoryName}
        iconSrc={category.iconUrl}
        iconColorVariant={category.iconColor}
        active={activeCategory === category.category}
        onClick={(active) => {
          appType === AppType.GAMES
            ? logger.logGameCategorySelect(category.category, active)
            : logger.logAppCategorySelect(category.category, active);
        }}
        testId="CategoryStringLink"
      />
    );

  return (
    <Scroller
      navButtonsScrollBy={300}
      disableDefaultScrollableContainerClass
      testId="categories"
      variant="full_width"
    >
      {categoryChunks.map((chunk, index) => (
        <span className={styles.row} key={index}>
          {index === 0 && (
            <>
              <Chip
                linkTo={appType === AppType.GAMES ? '/games' : '/'}
                label={INTERESTING_TITLE}
                iconComponent={SparkleIcon}
                active={!activeCategory}
                onClick={(active) => {
                  appType === AppType.GAMES
                    ? logger.logGameInterestingSelect(active)
                    : logger.logAppInterestingSelect(active);
                }}
              />
              <Chip
                linkTo={getLink(NO_CATEGORY_CATEGORY, appType) + getFullValue()}
                label={
                  appType === AppType.GAMES ? ALL_GAMES_TITLE : ALL_APPS_TITLE
                }
                iconComponent={AllCategoryIcon}
                active={activeCategory === NO_CATEGORY_CATEGORY}
                onClick={(active) => {
                  appType === AppType.GAMES
                    ? logger.logAllGamesClick(active)
                    : logger.logAllAppsClick(active);
                }}
                testId="CategoryStringLink"
              />
            </>
          )}
          {chunk.map(renderCategory)}
        </span>
      ))}
    </Scroller>
  );
};
